import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, take, throwError } from 'rxjs';
import { IFilterAPI } from '../standalones/components/generic-filters-view/filters.interface';

interface DataSourceConfig<T> {
  fetchStrategy: FetchStrategy;
  fetch: (params: DataSourceFetchParams) => Observable<DataSourceFetchResult<T>>;
  paginationStrategy: PaginationStrategy;
  pageSize: number;
}

// TODO: Add client-side strategy support.
export enum FetchStrategy {
  ServerSide = 'server-side',
}

export enum PaginationStrategy {
  ScrollBased = 'scroll-based',
  PageBased = 'page-based',
}

export type DataSourceConfigOptions<T> = Pick<DataSourceConfig<T>, 'fetch'> &
  Partial<DataSourceConfig<T>>;

export interface DataSourceFetchParams {
  filter: IFilterAPI;
  pageIndex: number;
  pageSize: number;
}

export interface DataSourceFetchResult<T> {
  items: T[];
  totalItems: number;
}

@Injectable()
export class DataSourceService<T> {
  private _isLoading = new BehaviorSubject<boolean>(true);
  private _isEmpty = new BehaviorSubject<boolean>(true);
  private _data = new BehaviorSubject<T[]>([]);
  private _totalItems = new BehaviorSubject<number>(0);
  private _pageIndex = new BehaviorSubject<number>(0);
  private _config: DataSourceConfig<T> = {
    fetchStrategy: FetchStrategy.ServerSide,
    fetch: () => throwError(() => new Error('Fetch function not set')),
    paginationStrategy: PaginationStrategy.PageBased,
    pageSize: 20,
  };

  get data(): T[] {
    return this._data.value;
  }

  get isLoading(): boolean {
    return this._isLoading.value;
  }

  get isEmpty(): boolean {
    return this._isEmpty.value;
  }

  get totalItems(): number {
    return this._totalItems.value;
  }

  get pageIndex(): number {
    return this._pageIndex.value;
  }

  get config(): DataSourceConfig<T> {
    return this._config;
  }

  get isLastPage(): boolean {
    return this.totalItems <= (this.pageIndex + 1) * this.config.pageSize;
  }

  get data$(): Observable<T[]> {
    return this._data.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoading.asObservable();
  }

  get isEmpty$(): Observable<boolean> {
    return this._isEmpty.asObservable();
  }

  get totalItems$(): Observable<number> {
    return this._totalItems.asObservable();
  }

  get pageIndex$(): Observable<number> {
    return this._pageIndex.asObservable();
  }

  configure(options: DataSourceConfigOptions<T>) {
    // This configures the config object following a priority order:
    // 1. First applies the default config.
    // 2. Then replaces any default config values with the optionally provided values.
    // Notice that because the fetch function is a required property, it will always be set on step 2.
    this._config = {
      ...this._config,
      ...options,
    };
  }

  setPageIndex(value: number) {
    this._pageIndex.next(value);
  }

  nextPageIndex() {
    this.setPageIndex(this.pageIndex + 1);
  }

  reset() {
    this._totalItems.next(0);
    this._data.next([]);
    this._isLoading.next(true);
    this._isEmpty.next(true);
    this.setPageIndex(0);
  }

  executeFetch(filter: IFilterAPI) {
    this._isLoading.next(true);

    let observable: Observable<DataSourceFetchResult<T>>;
    switch (this.config.fetchStrategy) {
      case FetchStrategy.ServerSide:
        observable = this.getServerSideFetch(filter);
        break;
    }

    observable.pipe(take(1)).subscribe({
      next: (result) => {
        this._isLoading.next(false);
        this._isEmpty.next(result.totalItems === 0);
        this._totalItems.next(result.totalItems);
        this._data.next(result.items);
      },
      error: (error) => {
        this._isLoading.next(false);
        console.error('DataSource fetch error:', error);
      },
    });
  }

  private getServerSideFetch(filter: IFilterAPI): Observable<DataSourceFetchResult<T>> {
    const params: DataSourceFetchParams = {
      filter,
      pageIndex: this.pageIndex,
      pageSize: this.config.pageSize,
    };

    return this.config.fetch(params).pipe(
      map((result) => ({
        items:
          this.config.paginationStrategy === PaginationStrategy.ScrollBased
            ? [...this.data, ...result.items]
            : result.items,
        totalItems: result.totalItems,
      })),
    );
  }
}
